import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ArrowLeftIcon } from '@/components/icons/icon-set'
import { Seo } from '@/components/generals/seo'
import { ForceTheme } from '@/components/generals/theme-provider'
import { VIEW_RENDERS } from '@/components/docs/view-registry'
import { VIEWS, getView } from '@/constants/views'

interface ViewPageProps {
    id: string
}

/* Full-screen view of one block or template. The docs frames load these for the width switch. */
const ViewPage: FC<ViewPageProps> = ({ id }): ReactNode => {
    const view = getView(id)
    const entry = VIEW_RENDERS[id]
    const [standalone, setStandalone] = useState(false)

    useEffect(() => {
        setStandalone(window.self === window.top)
    }, [])

    if (!view || !entry) return null

    return (
        <>
            <Seo title={view.title} description={view.description} />
            {view.dark && <ForceTheme theme='dark' />}

            {entry.padded
                ? (
                    <main className='min-h-dvh bg-surface-page p-4 text-text-primary sm:p-8'>
                        {entry.render()}
                    </main>
                )
                : entry.render()}

            {standalone && (
                <Link
                    href={`/docs/${view.group}/${view.category}#${view.id}`}
                    className='fixed bottom-4 left-4 z-40 inline-flex h-10 items-center gap-2 rounded-full border border-border-default bg-surface-overlay px-4 text-sm font-medium text-text-primary shadow-popover transition-colors outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus'
                >
                    <ArrowLeftIcon className='size-4' aria-hidden='true' />
                    Back to docs
                </Link>
            )}
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: VIEWS.map(view => ({ params: { id: view.id } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<ViewPageProps> = async ({ params }) => ({
    props: { id: String(params?.id) }
})

export default ViewPage
